<template>
  <Modal
    v-model:visible="isVisible"
    class="check-call-requests-modal"
    :header="t('carrierPortal.checkCallReminders.send')"
    :dismissable-mask="!isAwaitingEmailsSent"
    @keypress.enter="onConfirm"
    @keypress.esc="isAwaitingEmailsSent ? (isVisible = false) : ''"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="check-call-requests-emails"
      :errors="v$.emails.$errors"
      :label="t('shipments.emailsCommaSeparated')"
      class="field"
    >
      <InputText
        :id="inputId"
        v-model="modelValue.emails"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        autofocus
        :placeholder="t('shipments.emailsCommaSeparated')"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancelAndClose')"
        @click="isVisible = false"
      />

      <RygenButton
        :label="t('common.confirm')"
        icon="pi pi-check"
        :loading="isAwaitingEmailsSent"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';

import type { SendCheckCallRemindersPayload } from '@/stores/CheckCall';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const isVisible = defineModel<boolean>('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  emails: {
    type: String,
    default: () => '',
  },
});
const { emails } = toRefs(props);
const emit = defineEmits<{
  (e: 'submit', payload: SendCheckCallRemindersPayload): void;
}>();

const { t } = useI18n();
const { uid } = useUid('check-call-requests-modal');

const modelValue = ref({ emails: emails.value });
const rules = {
  emails: {
    required: required(t('common.email')),
  },
};
const { v$, validate } = useValidations(modelValue, rules, uid);
const isAwaitingEmailsSent = ref(false);

const onConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  isAwaitingEmailsSent.value = true;
  emit('submit', {
    emails: modelValue.value.emails,
    confirmCompletion: () => {
      isAwaitingEmailsSent.value = false;
      isVisible.value = false;
    },
  });
};

watch(isVisible, (newVal: boolean) => {
  if (newVal) {
    modelValue.value.emails = emails.value;
  }
});
</script>
